import type {ExpenseRow, PhaseBaselineExpenseRequest} from './BaselineTypes';

type ExpenseItem = Pick<
	ExpenseRow,
	'phaseBaselineExpenseId' | 'projectId' | 'categoryId' | 'phaseId' | 'deliverableId' | 'cost' | 'markup' | 'revenue' | 'name'
>;

/** The cost fields of the request: everything for a cost viewer, a 0 cost for anyone else's new line, else none. */
const costFields = (item: ExpenseItem, seesCost: boolean, isNew: boolean) => {
	if (seesCost) {
		return {expenseCost: item.cost, expenseMarkup: item.markup};
	}
	return isNew ? {expenseCost: 0} : {};
};

/**
 * The save request for one expense line. A viewer who can see cost sends cost and markup. Anyone else's screen has
 * no cost to send: on a line the server already holds, both are left out and the company service keeps the stored
 * cost; a new line sends a cost of 0 and no markup, so it is never stored without a cost, which a company service
 * that predates revenue-only saves cannot read back.
 *
 * `storedIds` are the ids of the lines the server returned. A line whose id is missing or not among them was added
 * on this screen and has its id from the browser, not the server.
 */
export const toPhaseBaselineExpenseRequest = (
	item: ExpenseItem,
	seesCost: boolean,
	storedIds: ReadonlySet<string>
): PhaseBaselineExpenseRequest => ({
	id: item.phaseBaselineExpenseId,
	projectId: item.projectId,
	expenseCategoryId: item.categoryId,
	phaseId: item.phaseId,
	deliverableId: item.deliverableId,
	expenseRevenue: item.revenue,
	expenseName: item.name,
	...costFields(item, seesCost, !item.phaseBaselineExpenseId || !storedIds.has(item.phaseBaselineExpenseId)),
});
